import {gql} from '@apollo/client';

import {
  QueryResultType,
  useSkippableBenchlingQuery,
  SkippableBenchlingQueryResult,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  SchemaInterfacesQuery,
  SchemaInterfacesQueryVariables,
} from 'molbio/bulk-assembly/assemble-modal/__generated/SchemaInterfacesQuery';
import {SchemaIdToImplementedInterfaces} from 'molbio/bulk-assembly/assemble-modal/polymerSchemaLinkCompatibility';

export const SCHEMA_INTERFACES_QUERY = gql`
  query SchemaInterfacesQuery($id: StrictString!) @skipAudit @engComponent(name: MOLBIO) {
    tagSchema(id: $id) {
      ... on TagSchemaNode {
        id
        interfaces {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

/**
 * Custom hook that fetches schema interface information for construct, fragment, and primer schemas.
 *
 * @param constructTagSchemaId - The ID of the construct tag schema to fetch interfaces for. If null, the query is skipped.
 * @param fragmentTagSchemaId - The ID of the fragment tag schema to fetch interfaces for. If null, the query is skipped.
 * @param primerTagSchemaId - The ID of the primer tag schema to fetch interfaces for. If null, the query is skipped.
 * @returns A mapping from schema ID to Set of interface IDs implemented by that schema.
 *
 * @description
 * This hook uses GraphQL to fetch the interfaces implemented by the provided tag schemas.
 * It runs multiple queries in parallel for efficiency and returns a consolidated mapping
 * of schema IDs to their implemented interface IDs. Individual queries are skipped when
 * their corresponding schema ID is null to avoid unnecessary API calls.
 */
export function useSchemaInterfaces(
  constructTagSchemaId: string | null,
  fragmentTagSchemaId: string | null,
  primerTagSchemaId: string | null
): SchemaIdToImplementedInterfaces {
  const constructQueryResult = useSkippableBenchlingQuery<
    SchemaInterfacesQuery,
    SchemaInterfacesQueryVariables
  >(SCHEMA_INTERFACES_QUERY, {
    variables: {id: constructTagSchemaId!},
    skip: !constructTagSchemaId,
  });
  const fragmentQueryResult = useSkippableBenchlingQuery<
    SchemaInterfacesQuery,
    SchemaInterfacesQueryVariables
  >(SCHEMA_INTERFACES_QUERY, {
    variables: {id: fragmentTagSchemaId!},
    skip: !fragmentTagSchemaId,
  });
  const primerQueryResult = useSkippableBenchlingQuery<SchemaInterfacesQuery, SchemaInterfacesQueryVariables>(
    SCHEMA_INTERFACES_QUERY,
    {
      variables: {id: primerTagSchemaId!},
      skip: !primerTagSchemaId,
    }
  );
  return [constructQueryResult, fragmentQueryResult, primerQueryResult].reduce(
    (acc: SchemaIdToImplementedInterfaces, result: SkippableBenchlingQueryResult<SchemaInterfacesQuery>) => {
      if (result.type === QueryResultType.SUCCESS && !isInaccessible(result.data.tagSchema)) {
        acc[result.data.tagSchema.id] = new Set(
          result.data.tagSchema.interfaces.map(({id: schemaInterfaceId}: {id: string}) => schemaInterfaceId)
        );
      }
      return acc;
    },
    {}
  );
}
